import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface PopoverProps {
  anchor?: HTMLElement | null
  open: boolean
  onClose: () => void
  children: ReactNode
  width?: number
  align?: 'left' | 'right'
}

export default function Popover({ open, onClose, children, width = 280 }: PopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (popoverRef.current && popoverRef.current.contains(target)) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    // Defer mousedown attach by one tick so the click that opened the
    // popover doesn't immediately trigger the outside-click handler.
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onDocClick)
    }, 0)
    document.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      role="dialog"
      style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
    >
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <div
        ref={popoverRef}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width,
          maxHeight: '80vh',
        }}
        className="bg-white border border-gray-200 rounded-lg shadow-xl p-2 overflow-y-auto"
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
